import React, { useState, useEffect, useMemo } from 'react';
import { useLogs } from '../hooks/useLogs';
import { LogRow } from '../components/LogRow';
import { supabaseAdmin } from '../lib/supabase';
import { Search, Activity, User, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

export function LogsPage() {
  const [adminEmail, setAdminEmail] = useState('');
  const [action, setAction] = useState('');
  const [dateRange, setDateRange] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [search, setSearch] = useState('');
  const [admins, setAdmins] = useState<any[]>([]);

  const filters = useMemo(() => ({
    adminEmail: adminEmail || undefined,
    action: action || undefined,
    dateRange,
    search: search || undefined,
  }), [adminEmail, action, dateRange, search]);

  const { logs, loading } = useLogs(filters);

  useEffect(() => {
    supabaseAdmin.from('admin_users').select('email, name').order('name')
      .then(({ data }) => setAdmins(data || []));
  }, []);

  // Stats
  const todayCount = logs.filter(l => {
    const today = new Date().toISOString().split('T')[0];
    return l.created_at.startsWith(today);
  }).length;

  const mostActive = useMemo(() => {
    const counts: Record<string, number> = {};
    logs.forEach(l => {
      counts[l.admin_email] = (counts[l.admin_email] || 0) + 1;
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted[0] ? { email: sorted[0][0], count: sorted[0][1] } : null;
  }, [logs]);

  const lastActivity = logs[0]
    ? formatDistanceToNow(new Date(logs[0].created_at), { addSuffix: true, locale: fr })
    : '-';

  return (
    <div>
      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={16} style={{ color: 'var(--accent)' }} />
            <div>
              <div className="stat-label">Actions</div>
              <div className="stat-value" style={{ fontSize: '22px' }}>{todayCount}</div>
              <div className="stat-sub">aujourd'hui</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <User size={16} style={{ color: 'var(--accent)' }} />
            <div>
              <div className="stat-label">Plus actif</div>
              <div className="stat-value" style={{ fontSize: '16px' }}>
                {mostActive ? mostActive.email.split('@')[0] : '-'}
              </div>
              <div className="stat-sub">{mostActive ? `${mostActive.count} actions` : '-'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} style={{ color: 'var(--accent)' }} />
            <div>
              <div className="stat-label">Dernière activité</div>
              <div className="stat-value" style={{ fontSize: '14px' }}>{lastActivity}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="filter-search">
          <Search size={16} />
          <input placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="filter-select" value={adminEmail} onChange={e => setAdminEmail(e.target.value)}>
          <option value="">Tous admins</option>
          {admins.map((a: any) => <option key={a.email} value={a.email}>{a.name}</option>)}
        </select>
        <select className="filter-select" value={action} onChange={e => setAction(e.target.value)}>
          <option value="">Toutes actions</option>
          <option value="login">Connexions</option>
          <option value="update_match">Modif. matchs</option>
          <option value="add_match">Ajout matchs</option>
          <option value="confirm_diffusion">Diffusions</option>
          <option value="delete_match">Suppressions</option>
          <option value="update_bar">Modif. bars</option>
        </select>
        <select className="filter-select" value={dateRange} onChange={e => setDateRange(e.target.value as any)}>
          <option value="today">Aujourd'hui</option>
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="all">Tout</option>
        </select>
      </div>

      {/* Logs timeline */}
      <div className="card">
        {loading ? (
          <div className="skeleton-list">
            {[1,2,3,4,5].map(i => <div key={i} className="skeleton-row" />)}
          </div>
        ) : logs.length === 0 ? (
          <p className="muted" style={{ padding: '20px' }}>Aucune action trouvée</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {logs.map(log => (
              <LogRow key={log.id} log={log} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
